import React, { useId } from 'react';

/**
 * Premium 'Liquid Glass' Brand Logo Mark.
 * Retains the signature 5-row bar segment identity but with advanced SVG aesthetics.
 */
export default function BrandLogoMark({ size, variant = 'onBrand', className = '' }) {
  const uid = useId().replace(/:/g, '');
  const isWatermark = variant === 'watermark';
  const numeric = typeof size === 'number';

  // Gradient / Filter IDs
  const gBase = `ft-base-${uid}`;
  const gGlass = `ft-glass-${uid}`;
  const gShine = `ft-shine-${uid}`;
  const gBars = `ft-bars-${uid}`;
  const fGlass = `ft-filter-${uid}`;

  return (
    <svg
      width={numeric ? size : undefined}
      height={numeric ? size : undefined}
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden
    >
      <defs>
        {/* Vibrant Liquid Blue Gradient */}
        <linearGradient id={gBase} x1="4" y1="4" x2="36" y2="36" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#60a5fa" />
          <stop offset="50%" stopColor="#2563eb" />
          <stop offset="100%" stopColor="#1d4ed8" />
        </linearGradient>

        {/* Translucent Glass Gradient */}
        <linearGradient id={gGlass} x1="2" y1="2" x2="38" y2="38" gradientUnits="userSpaceOnUse">
          <stop stopColor="white" stopOpacity="0.4" />
          <stop offset="50%" stopColor="white" stopOpacity="0.05" />
          <stop offset="100%" stopColor="white" stopOpacity="0.15" />
        </linearGradient>

        {/* Soft Shine Mask */}
        <linearGradient id={gShine} x1="8" y1="8" x2="28" y2="28" gradientUnits="userSpaceOnUse">
          <stop stopColor="white" stopOpacity="0.5" />
          <stop offset="100%" stopColor="white" stopOpacity="0" />
        </linearGradient>

        {/* Crisp White Bars with subtle depth */}
        <linearGradient id={gBars} x1="8" y1="9" x2="8" y2="33.25" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="100%" stopColor="#f8fafc" />
        </linearGradient>

        {/* Drop shadow / Glow filter for depth */}
        <filter id={fGlass} x="-5" y="-5" width="50" height="50" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
          <feFlood floodOpacity="0" result="BackgroundImageFix"/>
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
          <feOffset dy="1.5"/>
          <feGaussianBlur stdDeviation="2"/>
          <feComposite in2="hardAlpha" operator="out"/>
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0"/>
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow"/>
          <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape"/>
        </filter>
      </defs>

      <g filter={isWatermark ? undefined : `url(#${fGlass})`}>
        {/* Main Squircle Body */}
        <rect
          x="3"
          y="3"
          width="34"
          height="34"
          rx="10"
          fill={isWatermark ? 'white' : `url(#${gBase})`}
          fillOpacity={isWatermark ? 0.08 : 1}
        />
        
        {/* Glass Stroke Refinement */}
        <rect
          x="3.25"
          y="3.25"
          width="33.5"
          height="33.5"
          rx="9.75"
          stroke="white"
          strokeOpacity={isWatermark ? 0.1 : 0.25}
          strokeWidth="0.5"
          fill="none"
        />

        {/* Liquid Glass Overlay Piece */}
        {!isWatermark && (
          <rect
            x="3"
            y="3"
            width="34"
            height="34"
            rx="10"
            fill={`url(#${gGlass})`}
          />
        )}

        {/* Brand Bars: 5-row logic retained */}
        <g fill={isWatermark ? 'white' : `url(#${gBars})`} opacity={isWatermark ? 0.6 : 1}>
          {/* Row 1: short left, long right */}
          <rect x="9" y="10" width="8" height="3" rx="1.5" />
          <rect x="19" y="10" width="12" height="3" rx="1.5" />
          
          {/* Row 2: full width */}
          <rect x="9" y="15" width="22" height="3" rx="1.5" />
          
          {/* Row 3: center focal pill */}
          <rect x="15.5" y="20" width="9" height="3" rx="1.5" />
          
          {/* Row 4: full width */}
          <rect x="9" y="25" width="22" height="3" rx="1.5" />
          
          {/* Row 5: long left, short right */}
          <rect x="9" y="30" width="12" height="3" rx="1.5" />
          <rect x="23" y="30" width="8" height="3" rx="1.5" />
        </g>

        {/* Final Specular Gloss (Top Left Edge) */}
        {!isWatermark && (
           <path 
             d="M10 4.5 C 6 4.5, 4.5 6, 4.5 10" 
             stroke="white" 
             strokeOpacity="0.6" 
             strokeWidth="0.8" 
             strokeLinecap="round" 
           />
        )}
      </g>
    </svg>
  );
}
